import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { Button } from "@/components/ui/button";
import { useSliders } from "../hooks/use-sliders";
import { cn } from "@/lib/utils";
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';

export const HeroCarousel = () => {
  const { data: sliders = [], isLoading } = useSliders();
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Separate Embla instances for mobile and desktop
  const [mobileRef, mobileApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      skipSnaps: false,
      slidesToScroll: 1,
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  const [desktopRef, desktopApi] = useEmblaCarousel(
    {
      loop: true,
      align: "center",
      skipSnaps: false,
      slidesToScroll: 1,
    },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  const scrollPrev = useCallback(() => {
    mobileApi?.scrollPrev();
    desktopApi?.scrollPrev();
  }, [mobileApi, desktopApi]);

  const scrollNext = useCallback(() => {
    mobileApi?.scrollNext();
    desktopApi?.scrollNext();
  }, [mobileApi, desktopApi]);

  const scrollTo = useCallback(
    (index: number) => {
      mobileApi?.scrollTo(index);
      desktopApi?.scrollTo(index);
    },
    [mobileApi, desktopApi]
  );

  const onMobileSelect = useCallback(() => {
    if (!mobileApi) return;
    setSelectedIndex(mobileApi.selectedScrollSnap());
  }, [mobileApi]);

  const onDesktopSelect = useCallback(() => {
    if (!desktopApi) return;
    setSelectedIndex(desktopApi.selectedScrollSnap());
  }, [desktopApi]);

  useEffect(() => {
    if (!mobileApi) return;
    onMobileSelect();
    mobileApi.on("select", onMobileSelect);
    mobileApi.on("reInit", onMobileSelect);
    return () => {
      mobileApi.off("select", onMobileSelect);
      mobileApi.off("reInit", onMobileSelect);
    };
  }, [mobileApi, onMobileSelect]);

  useEffect(() => {
    if (!desktopApi) return;
    onDesktopSelect();
    desktopApi.on("select", onDesktopSelect);
    desktopApi.on("reInit", onDesktopSelect);
    return () => {
      desktopApi.off("select", onDesktopSelect);
      desktopApi.off("reInit", onDesktopSelect);
    };
  }, [desktopApi, onDesktopSelect]);

  if (isLoading) {
    return (
      <div className="relative w-full">
        <div className="w-full aspect-[2/1] bg-muted animate-pulse" />
      </div>
    );
  }

  if (sliders.length === 0) {
    return null;
  }

  return (
    <div className="relative w-full group">
      {/* Mobile/Portrait Tablet (< 1024px): Single full-width slides */}
      <div className="block lg:hidden">
        <div className="overflow-hidden" ref={mobileRef}>
          <div className="flex">
            {sliders.map((slider, index) => (
              <div
                key={slider.id ?? `${slider.SliderTitle}-${index}`}
                className="flex-none w-full min-w-0"
              >
                <div className="relative">
                  <img
                    src={slider.SliderImage.url}
                    alt={
                      slider.SliderImage.alternativeText || slider.SliderTitle
                    }
                    className="w-full h-auto"
                  />
                  {slider.SliderLinked && slider.SliderLink && (
                    <a
                      href={slider.SliderLink}
                      className="absolute inset-0"
                      aria-label={slider.SliderTitle}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile Navigation Arrows */}
        {sliders.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black hover:text-black z-10 h-10 w-10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={scrollPrev}
              aria-label="Previous slide"
            >
              <IconChevronLeft className="h-6 w-6 text-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black hover:text-black z-10 h-10 w-10 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={scrollNext}
              aria-label="Next slide"
            >
              <IconChevronRight className="h-6 w-6 text-foreground" />
            </Button>
          </>
        )}
      </div>

      {/* Desktop (>= 1024px): Three-panel peek design */}
      <div className="hidden lg:block">
        <div className="overflow-hidden" ref={desktopRef}>
          <div className="flex">
            {sliders.map((slider, index) => (
              <div
                key={slider.id ?? `${slider.SliderTitle}-${index}`}
                className={cn(
                  "flex-none min-w-0 transition-opacity duration-300",
                  // Center slide is 64%, side peeks are 18% each
                  "w-[64%] mr-[15px]",
                  index === selectedIndex ? "opacity-100" : "opacity-70"
                )}
              >
                <div className="relative">
                  <img
                    src={slider.SliderImage.url}
                    alt={
                      slider.SliderImage.alternativeText || slider.SliderTitle
                    }
                    className="w-full h-auto"
                  />
                  {slider.SliderLinked && slider.SliderLink && (
                    <a
                      href={slider.SliderLink}
                      className="absolute inset-0"
                      aria-label={slider.SliderTitle}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop Navigation Arrows */}
        {sliders.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black hover:text-black z-30 h-12 w-12 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={scrollPrev}
              aria-label="Previous slide"
            >
              <IconChevronLeft className="h-8 w-8 text-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black hover:text-black z-30 h-12 w-12 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={scrollNext}
              aria-label="Next slide"
            >
              <IconChevronRight className="h-8 w-8 text-foreground" />
            </Button>
          </>
        )}
      </div>

      {/* Dots indicator */}
      {sliders.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-30">
          {sliders.map((_, index) => (
            <button
              key={index}
              className={cn(
                "h-4 rounded-full transition-all duration-300",
                index === selectedIndex
                  ? "w-8 bg-[#fafafa]"
                  : "w-4 bg-white/60 hover:bg-white/80"
              )}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};
